import React, { useState, useEffect } from 'react';
import { 
  Brain, 
  TrendingUp, 
  AlertTriangle, 
  Target,
  BarChart3,
  Lightbulb,
  Clock,
  MapPin
} from 'lucide-react';
import { Emergency, Responder } from '../types';
import { AIEmergencyEngine } from '../utils/aiEngine';
import { EnhancedMatchingEngine } from '../utils/enhancedMatchingEngine';

interface AIInsightsDashboardProps {
  emergencies: Emergency[];
  responders: Responder[];
  className?: string;
}

export const AIInsightsDashboard: React.FC<AIInsightsDashboardProps> = ({
  emergencies,
  responders,
  className = ''
}) => {
  const [insights, setInsights] = useState<any>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [selectedInsight, setSelectedInsight] = useState<string>('patterns');

  const aiEngine = new AIEmergencyEngine();
  const matchingEngine = new EnhancedMatchingEngine();

  useEffect(() => {
    loadInsights();
  }, [emergencies, responders]);

  const loadInsights = async () => {
    setIsLoading(true);
    try {
      const [patterns, emergencyInsights] = await Promise.all([
        aiEngine.analyzeEmergencyPatterns(emergencies),
        matchingEngine.generateEmergencyInsights(emergencies)
      ]);

      setInsights({
        patterns,
        predictions: emergencyInsights.predictions,
        recommendations: emergencyInsights.recommendations,
        resourceOptimization: generateResourceOptimization(),
        performanceMetrics: calculatePerformanceMetrics()
      });
    } catch (error) {
      console.error('Failed to load AI insights:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const generateResourceOptimization = () => {
    const availableResponders = responders.filter(r => r.isAvailable).length;
    const totalResponders = responders.length;
    const utilization = ((totalResponders - availableResponders) / totalResponders) * 100;

    return {
      currentUtilization: utilization,
      optimalUtilization: 75,
      recommendations: [
        utilization > 80 ? 'Consider activating additional responders' : 'Current staffing levels adequate',
        'Redistribute resources based on predicted demand patterns',
        'Implement predictive deployment strategies'
      ]
    };
  };

  const calculatePerformanceMetrics = () => {
    const activeEmergencies = emergencies.filter(e => e.status !== 'resolved');
    const resolvedEmergencies = emergencies.filter(e => e.status === 'resolved');
    
    const avgResponseTime = resolvedEmergencies.reduce((acc, e) => {
      return acc + (e.resolutionTime || 0);
    }, 0) / (resolvedEmergencies.length || 1);

    return {
      activeEmergencies: activeEmergencies.length,
      resolutionRate: (resolvedEmergencies.length / emergencies.length) * 100,
      averageResponseTime: avgResponseTime,
      criticalEmergencies: emergencies.filter(e => e.severity === 'critical').length
    };
  };

  if (isLoading) {
    return (
      <div className={`bg-white rounded-lg shadow-md p-6 ${className}`}>
        <div className="flex items-center justify-center h-64">
          <div className="flex items-center gap-3">
            <Brain className="w-8 h-8 text-blue-600 animate-pulse" />
            <span className="text-lg text-gray-600">Analyzing emergency patterns...</span>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className={`bg-white rounded-lg shadow-md ${className}`}>
      <div className="p-6 border-b bg-gradient-to-r from-blue-50 to-purple-50">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 bg-blue-600 rounded-full flex items-center justify-center">
            <Brain className="w-6 h-6 text-white" />
          </div>
          <div>
            <h3 className="text-xl font-bold text-gray-900">AI Emergency Insights</h3>
            <p className="text-gray-600">Predictive analytics and optimization recommendations</p>
          </div>
        </div>
      </div>

      {/* Insight Tabs */}
      <div className="border-b">
        <nav className="flex">
          {[
            { id: 'patterns', label: 'Patterns', icon: TrendingUp },
            { id: 'predictions', label: 'Predictions', icon: Target },
            { id: 'optimization', label: 'Optimization', icon: BarChart3 },
            { id: 'recommendations', label: 'Recommendations', icon: Lightbulb }
          ].map(tab => {
            const TabIcon = tab.icon;
            return (
              <button
                key={tab.id}
                onClick={() => setSelectedInsight(tab.id)}
                className={`flex items-center gap-2 px-6 py-3 border-b-2 font-medium text-sm transition-colors ${
                  selectedInsight === tab.id
                    ? 'border-blue-500 text-blue-600 bg-blue-50'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:bg-gray-50'
                }`}
              >
                <TabIcon className="w-4 h-4" />
                {tab.label}
              </button>
            );
          })}
        </nav>
      </div>

      {/* Content */}
      <div className="p-6">
        {selectedInsight === 'patterns' && (
          <div className="space-y-6">
            <h4 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
              <TrendingUp className="w-5 h-5 text-blue-600" />
              Emergency Patterns Analysis
            </h4>
            
            {insights?.patterns?.patterns?.map((pattern: any, index: number) => (
              <div key={index} className="bg-gray-50 rounded-lg p-4">
                <div className="flex items-start justify-between mb-3">
                  <div>
                    <h5 className="font-medium text-gray-900 capitalize">{pattern.type} Emergencies</h5>
                    <p className="text-sm text-gray-600">{pattern.location}</p>
                  </div>
                  <div className="text-right">
                    <div className="text-sm font-medium text-blue-600">
                      {Math.round(pattern.frequency * 100)}% frequency
                    </div>
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-4 text-sm">
                  <div className="flex items-center gap-2">
                    <Clock className="w-4 h-4 text-gray-400" />
                    <span>{pattern.timePattern}</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <AlertTriangle className="w-4 h-4 text-orange-500" />
                    <span>{pattern.prediction}</span>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}

        {selectedInsight === 'predictions' && (
          <div className="space-y-6">
            <h4 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
              <Target className="w-5 h-5 text-purple-600" />
              Predictive Analytics
            </h4>
            
            {insights?.predictions?.map((prediction: any, index: number) => (
              <div key={index} className="border-l-4 border-purple-500 bg-purple-50 p-4 rounded-r-lg">
                <div className="flex items-start justify-between mb-2">
                  <h5 className="font-medium text-gray-900 capitalize">
                    {prediction.type.replace('_', ' ')}
                  </h5>
                  <div className="text-sm font-medium text-purple-600">
                    {Math.round(prediction.confidence * 100)}% confidence
                  </div>
                </div>
                <p className="text-gray-700 mb-2">{prediction.prediction}</p>
                <div className="flex items-center gap-2 text-sm text-gray-600">
                  <Clock className="w-4 h-4" />
                  <span>Timeframe: {prediction.timeframe}</span>
                </div>
              </div>
            ))}
          </div>
        )}

        {selectedInsight === 'optimization' && (
          <div className="space-y-6">
            <h4 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
              <BarChart3 className="w-5 h-5 text-green-600" />
              Resource Optimization
            </h4>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="bg-green-50 rounded-lg p-4">
                <h5 className="font-medium text-gray-900 mb-3">Current Utilization</h5>
                <div className="flex items-center gap-3">
                  <div className="flex-1 bg-gray-200 rounded-full h-3">
                    <div 
                      className="bg-green-600 h-3 rounded-full transition-all duration-500"
                      style={{ width: `${insights?.resourceOptimization?.currentUtilization || 0}%` }}
                    />
                  </div>
                  <span className="text-sm font-medium">
                    {Math.round(insights?.resourceOptimization?.currentUtilization || 0)}%
                  </span>
                </div>
                <p className="text-sm text-gray-600 mt-2">
                  Target: {insights?.resourceOptimization?.optimalUtilization}%
                </p>
              </div>

              <div className="bg-blue-50 rounded-lg p-4">
                <h5 className="font-medium text-gray-900 mb-3">Performance Metrics</h5>
                <div className="space-y-2 text-sm">
                  <div className="flex justify-between">
                    <span>Active Emergencies:</span>
                    <span className="font-medium">{insights?.performanceMetrics?.activeEmergencies}</span>
                  </div>
                  <div className="flex justify-between">
                    <span>Resolution Rate:</span>
                    <span className="font-medium">{Math.round(insights?.performanceMetrics?.resolutionRate || 0)}%</span>
                  </div>
                  <div className="flex justify-between">
                    <span>Avg Response Time:</span>
                    <span className="font-medium">{Math.round(insights?.performanceMetrics?.averageResponseTime || 0)}min</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        {selectedInsight === 'recommendations' && (
          <div className="space-y-6">
            <h4 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
              <Lightbulb className="w-5 h-5 text-yellow-600" />
              AI Recommendations
            </h4>
            
            <div className="space-y-3">
              {insights?.recommendations?.map((recommendation: string, index: number) => (
                <div key={index} className="flex items-start gap-3 p-3 bg-yellow-50 rounded-lg">
                  <Lightbulb className="w-5 h-5 text-yellow-600 mt-0.5 flex-shrink-0" />
                  <p className="text-gray-700">{recommendation}</p>
                </div>
              ))}
            </div>

            <div className="mt-6 p-4 bg-gradient-to-r from-blue-50 to-purple-50 rounded-lg">
              <h5 className="font-medium text-gray-900 mb-2">Next Steps</h5>
              <ul className="text-sm text-gray-700 space-y-1">
                <li>• Review and implement high-priority recommendations</li>
                <li>• Monitor pattern changes and adjust strategies accordingly</li>
                <li>• Schedule regular AI insights review meetings</li>
                <li>• Update responder training based on identified patterns</li>
              </ul>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};